import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { nav } from "../../data/site";
import PillButton from "../common/PillButton";
import { useStaffing } from "../forms/StaffingProvider";

function Logo() {
  return (
    <Link to="/" className="group flex items-center" data-testid="nav-logo" aria-label="AMVI Technologies home">
      <img src="/amvi-wordmark-v2.png" alt="AMVI Technologies logo" className="h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03] sm:h-14" />
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { open } = useStaffing();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Logo />

        <div className="hidden items-center gap-1 xl:flex">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `group relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-sky-400" : "text-slate-300 hover:text-white"
                }`
              }
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`pointer-events-none absolute inset-x-3.5 -bottom-0.5 h-px origin-left bg-gradient-to-r from-sky-400 to-cyan-300 transition-transform duration-300 ${
                      isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          <PillButton variant="outline" size="md" arrow={false} onClick={open} testId="nav-find-talent">
            Find Talent
          </PillButton>
          <PillButton to="/contact" size="md" testId="nav-talk-expert">
            Talk to an Expert
          </PillButton>
        </div>

        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white xl:hidden"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="glass overflow-hidden xl:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-6 pb-6 pt-2">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-white/5 text-sky-400" : "text-slate-200 hover:bg-white/5"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="flex flex-col gap-3 pt-4">
                <PillButton variant="outline" arrow={false} onClick={open} className="w-full">
                  Find Talent
                </PillButton>
                <PillButton to="/contact" className="w-full">
                  Talk to an Expert
                </PillButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
